import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PUBLIC: fetch the visibility + display-order map keyed by section index.
 * Returned shape: `{ "0": { hidden: false, order: 0 }, ... }`
 * Missing indexes fall back to visible + order === index in the caller.
 */
export const fetchSettings = async () => {
  const { data } = await axios.get(`${API}/settings`);
  return data || {};
};

/**
 * Verify the admin password. Returns the admin token on success — the client
 * then stores it under `admin-token` in localStorage and sends it back with
 * every write via the existing `X-Admin-Token` header.
 */
export const adminLogin = async (password) => {
  const { data } = await axios.post(`${API}/admin/login`, { password });
  return data; // { ok, token }
};

/**
 * Bulk-save the visibility + order for all projects. Payload:
 *   { settings: [{ index, hidden, order }, ...] }
 */
export const saveSettings = async (settings, token) => {
  const { data } = await axios.put(
    `${API}/admin/settings`,
    { settings },
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};

// ---------- Custom projects (admin-created via /admin) ----------

/** PUBLIC — list admin-created projects. */
export const fetchCustomProjects = async () => {
  const { data } = await axios.get(`${API}/projects/custom`);
  return Array.isArray(data) ? data : [];
};

/** Create a new custom project. Pass full payload (or omit fields to accept
 * server defaults). For "duplicate", the caller sends the source project's
 * fields verbatim so the backend stays agnostic to duplication sources. */
export const createCustomProject = async (payload, token) => {
  const { data } = await axios.post(`${API}/admin/projects`, payload, {
    headers: { "X-Admin-Token": token },
  });
  return data;
};

/** Partial update — send only the fields you want to change. Also used
 * to persist per-card `hidden` + `order` for custom projects during the
 * admin Save pass (in parallel with the bulk static settings upsert). */
export const updateCustomProject = async (id, payload, token) => {
  const { data } = await axios.put(`${API}/admin/projects/${id}`, payload, {
    headers: { "X-Admin-Token": token },
  });
  return data;
};

export const deleteCustomProject = async (id, token) => {
  const { data } = await axios.delete(`${API}/admin/projects/${id}`, {
    headers: { "X-Admin-Token": token },
  });
  return data;
};

/**
 * Upload a media file (image or video) to the backend, which proxies it
 * to Emergent object storage and returns `{ id, url, media_type, ... }`.
 * The returned `url` is `/api/media/{id}` — a stable, cache-immutable
 * proxied route this backend serves to public visitors. Store that URL
 * on the custom project's `media_src` field.
 *
 * `onProgress` optionally receives an axios ProgressEvent so the caller
 * can render an inline "uploading… 43%" indicator on the card.
 */
export const uploadMedia = async (file, token, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/upload`, form, {
    headers: {
      "X-Admin-Token": token,
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: onProgress,
  });
  return data;
};

// ---------- Static project overrides (title + hero media for the base 27) ----------

/** PUBLIC — returns `{ [index]: { title?, media_type?, media_src?, media_poster? } }` */
export const fetchStaticOverrides = async () => {
  const { data } = await axios.get(`${API}/static_overrides`);
  return data || {};
};

/** Upsert a partial override for a single static project index. */
export const updateStaticOverride = async (index, patch, token) => {
  const { data } = await axios.put(`${API}/admin/static_overrides/${index}`, patch, {
    headers: { "X-Admin-Token": token },
  });
  return data;
};

/** Reset a static project override — restores the baked-in defaults. */
export const deleteStaticOverride = async (index, token) => {
  const { data } = await axios.delete(`${API}/admin/static_overrides/${index}`, {
    headers: { "X-Admin-Token": token },
  });
  return data;
};

// ---------- Case-study frame overrides (per-project sub-page edits) ----------

/** PUBLIC — returns `{ [slug]: { [frame_number]: override } }`. Frames not
 *  present here fall back to the source-code FRAME_OVERRIDES / editorial
 *  defaults on the client. */
export const fetchCaseStudyOverrides = async () => {
  const { data } = await axios.get(`${API}/projects/case_study_overrides`);
  return data || {};
};

/** Upsert a full override doc for one frame. `payload` must include `type`
 *  (`editorial` | `vimeo` | `image` | `titledImage` | `skip`) plus any
 *  fields relevant to that type — the backend replaces the whole doc so
 *  omitted fields are cleared. */
export const updateCaseStudyFrame = async (slug, frameNumber, payload, token) => {
  const { data } = await axios.put(
    `${API}/admin/case_study_overrides/${slug}/${frameNumber}`,
    payload,
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};

/** Reset a single frame back to the baked-in default. */
export const deleteCaseStudyFrame = async (slug, frameNumber, token) => {
  const { data } = await axios.delete(
    `${API}/admin/case_study_overrides/${slug}/${frameNumber}`,
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};

/** Reset EVERY admin edit for a case study. */
export const resetCaseStudy = async (slug, token) => {
  const { data } = await axios.delete(
    `${API}/admin/case_study_overrides/${slug}`,
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};


// ---------- Typography (project-headline appearance) ----------

/** PUBLIC — fetch the current project-headline typography settings. Always
 * returns a full 5-field object (backend merges defaults). */
export const fetchTypography = async () => {
  const { data } = await axios.get(`${API}/settings/typography`);
  return data;
};

/** Admin — persist typography settings. Payload keys:
 *   font_family (str), font_size (px int), font_weight (100-900 step 100),
 *   line_height (float), letter_spacing (em, float).
 */
export const saveTypography = async (settings, token) => {
  const { data } = await axios.put(
    `${API}/admin/settings/typography`,
    settings,
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};

/** Admin — Reset to Default button. Deletes the stored doc so the next
 * GET returns the baked-in defaults. */
export const resetTypography = async (token) => {
  const { data } = await axios.delete(
    `${API}/admin/settings/typography`,
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};

// ---------- Typography presets (admin-only) ----------

/** Admin — list saved typography presets (newest first). */
export const fetchTypographyPresets = async (token) => {
  const { data } = await axios.get(`${API}/admin/settings/typography/presets`, {
    headers: { "X-Admin-Token": token },
  });
  return data || [];
};

/** Admin — save the current typography as a named preset.
 *  Payload extends the typography schema with a `name` string. */
export const createTypographyPreset = async (preset, token) => {
  const { data } = await axios.post(
    `${API}/admin/settings/typography/presets`,
    preset,
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};

/** Admin — delete a saved preset by id. */
export const deleteTypographyPreset = async (id, token) => {
  const { data } = await axios.delete(
    `${API}/admin/settings/typography/presets/${id}`,
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};


// ---------- Asset Library ----------

/** Admin — list every image asset (newest first). Returns array of
 * `{ id, url, filename, content_type, size, width, height, created_at }`.
 * `url` is the stable /api/media/{id} proxy — safe to persist and embed. */
export const fetchAssets = async (token) => {
  const { data } = await axios.get(`${API}/admin/assets`, {
    headers: { "X-Admin-Token": token },
  });
  return data || [];
};

/** Admin — soft-delete an asset. Object stays in storage; the library
 * simply hides it from GET /admin/assets. Any projects still referencing
 * the URL keep working because /api/media/{id} continues to serve until
 * the row is hard-deleted (not currently exposed via API). */
export const deleteAsset = async (id, token) => {
  const { data } = await axios.delete(`${API}/admin/assets/${id}`, {
    headers: { "X-Admin-Token": token },
  });
  return data;
};

/** Admin — scan the portfolio source + DB overrides for every image URL
 * currently in use and register any new ones in the Asset Library.
 * Idempotent (dedups on URL). Returns `{ imported, skipped, total }`. */
export const importExistingAssets = async (token) => {
  const { data } = await axios.post(
    `${API}/admin/assets/import-existing`,
    {},
    { headers: { "X-Admin-Token": token } }
  );
  return data;
};
